import { useCallback, useState } from "react";

/**
 * Which station this SCREEN is showing.
 *
 * Per DEVICE, not per user, and that is a domain decision rather than a
 * shortcut (§6 of the kitchen design): one cook works grill on Tuesday and cold
 * on Wednesday, so binding the choice to their account would mean re-picking on
 * every shift — while the screen bolted to the wall above the grill is showing
 * the grill for as long as it hangs there. It is the same reasoning, and the
 * same storage, as the till's own theme preference in `shared/posTheme.ts`.
 *
 * `null` means "every station", which is what a single-screen kitchen wants and
 * what the board's endpoint returns when `stationId` is omitted.
 */

const STORAGE_KEY = "triotyKitchenStation";

/** The sentinel written for "every station" — `null` cannot be stored. */
const ALL_STATIONS = "__all__";

function read(): string | null {
  // Wrapped, like every other localStorage read in this app: a private window,
  // cleared site data, or a browser set to block storage all THROW on access
  // rather than returning null, and an unhandled throw here would take the
  // whole board down on first paint.
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === null || value === ALL_STATIONS ? null : value;
  } catch {
    return null;
  }
}

export function useStationPreference(): {
  stationId: string | null;
  setStationId: (next: string | null) => void;
  forget: () => void;
} {
  const [stationId, setStored] = useState<string | null>(read);

  const setStationId = useCallback((next: string | null) => {
    setStored(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ?? ALL_STATIONS);
    } catch {
      // A screen that cannot persist the choice still honours it for this
      // session. Losing the preference on the next reload is annoying; failing
      // the tap mid-service is worse.
    }
  }, []);

  /**
   * Drop a saved choice that no longer names a real station — a station that
   * was deleted, or a tablet carried to another restaurant on the same account.
   *
   * Storage ONLY: it deliberately does not touch the React state. The board
   * decides that the id is dead by comparing it against the station list the
   * server just sent, and it keeps showing "the station you had is gone" for
   * as long as that stays true. Clearing the state here would make the
   * condition false again and the explanation would vanish on the next paint,
   * leaving a board silently showing more than the device asked for.
   */
  const forget = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Same as above: an unwritable store is not worth failing the screen.
    }
  }, []);

  return { stationId, setStationId, forget };
}
